import { type ReactNode, useRef, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Track, VrQualityPreset } from '@mindcloudxai_3d/core';
import { Button } from '@heroui/react';
import { RendererController } from '@renderer/index';
import { useUrlParams } from '@hooks/useUrlParams';
import { useViewerStore } from '@stores/viewerStore';
import { HUDLayout } from '@/modules/toolbar/HUDLayout';
import { CinematicProgressBar } from '@/modules/cinematic/CinematicProgressBar';
import { TrackEditor, TrackPreview, useRaceTrackStore } from '@/modules/race-track';
import { HotspotLayer } from '@/modules/hotspots';
import { SceneOfflinePage } from './SceneOfflinePage';
import { PreviewExpiredPage } from './PreviewExpiredPage';
import { SceneNotFoundPage } from './SceneNotFoundPage';
import { useCinematicEvents } from './hooks/useCinematicEvents';
import { useHotspotNavigation } from './hooks/useHotspotNavigation';
import { useMeasureSync } from './hooks/useMeasureSync';
import { useNavigationController } from './hooks/useNavigationController';
import { useSceneSession } from './hooks/useSceneSession/useSceneSession';
import { bindViewerEvents } from './hooks/useSceneSession/viewerEvents';
import { useSnapshotCapture } from './hooks/useSnapshotCapture';
import { useImmersiveMode } from './hooks/useImmersiveMode';
import { useAssetPick } from './hooks/useAssetPick';
import { FileDropZone } from './FileDropZone';
import type { PublishStatus } from '@/types';

/** 发布状态门控：非 published 时展示对应错误页。 */
function renderPublishGate(publishStatus: PublishStatus): ReactNode {
  switch (publishStatus) {
    case 'published':
      return null;
    case 'unpublished':
      return <SceneOfflinePage />;
    case 'preview':
      return <PreviewExpiredPage />;
    // K12: 无登录限制，private 状态不再拦截，回退到默认（不展示门控页）
    case 'private':
      return null;
    default:
      return null;
  }
}

function buildWebglUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.set('deviceType', 'webgl');
  return url.toString();
}

interface VrWebglDialogProps {
  onCancel: () => void;
  onConfirm: () => void;
}

function VrWebglDialog({ onCancel, onConfirm }: VrWebglDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (el && !el.open) el.showModal();
    return () => {
      if (el?.open) el.close();
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="vr-webgl-dialog-title"
      className="pointer-events-auto rounded-3xl border border-white/20 bg-slate-950/90 p-6 text-white shadow-2xl backdrop:bg-black/55 backdrop:backdrop-blur-sm"
      style={{ maxWidth: 'min(420px, calc(100vw - 32px))' }}
    >
      <h2 id="vr-webgl-dialog-title" className="text-lg font-semibold">
        {t('toolbar.vrWebglTitle')}
      </h2>
      <p className="mt-3 text-sm leading-6 text-white/70">{t('toolbar.vrWebglDesc')}</p>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" className="rounded-full px-5" onPress={onCancel}>
          {t('toolbar.cancel')}
        </Button>
        <Button variant="primary" className="rounded-full px-5" onPress={onConfirm}>
          {t('toolbar.vrWebglConfirm')}
        </Button>
      </div>
    </dialog>
  );
}

export function ExplorePage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<RendererController | null>(null);
  // 当前会话的运镜轨迹（mock 优先）；播放由 viewer 闭环，这里只留数据用于 UI 门控
  const trackRef = useRef<Track | null>(null);
  const [webglDialogOpen, setWebglDialogOpen] = useState(false);
  const urlParams = useUrlParams();
  const { mockTrack, trackDuration, renderer, deviceType, ministats } = urlParams;

  const sceneConfig = useViewerStore((s) => s.sceneConfig);
  const publishStatus = useViewerStore((s) => s.publishStatus);

  // 发布状态门控：加载完成后若非 published，展示对应错误页。
  const publishGate = renderPublishGate(publishStatus);

  // renderer 会话会重建，覆盖层需延迟读取当前 controller。
  const getController = useCallback(() => controllerRef.current, []);

  // 场景会话生命周期（init→fetch→load→bind events）收缩为独立 hook
  const { handleRetry } = useSceneSession({
    containerRef,
    controllerRef,
    trackRef,
    urlParams,
  });

  const handleToggleCinematic = useCallback(() => {
    const ctrl = controllerRef.current;
    // 无业务轨迹时不响应，避免触发 viewer 内置兜底轨迹
    if (!ctrl || !trackRef.current) return;
    // 直接走 viewer 内部 playPause 事件链路，与 Space 快捷键同语义；
    // 不分别调 playAnim/pauseAnim，避免与 ModeShortcuts 双重触发冲突。
    ctrl.navigation.anim.togglePlayPause();
  }, []);

  // VR 入口：非 WebGL 时先弹窗切 WebGL；已是 WebGL 时返回 true 由 InfoPill 弹出档位浮层
  const handleEnterVr = useCallback((): boolean => {
    if (renderer !== 'playcanvas' || deviceType !== 'webgl') {
      setWebglDialogOpen(true);
      return false;
    }
    return true;
  }, [deviceType, renderer]);

  const handleSelectVrQuality = useCallback((preset: VrQualityPreset) => {
    controllerRef.current?.xr.enterVr(preset);
  }, []);

  const handleConfirmWebglReload = useCallback(() => {
    window.location.assign(buildWebglUrl());
  }, []);

  // PLY 导入时若当前为 WebGPU，重新初始化 renderer 为 WebGL 并重新绑定 viewer 事件
  const offViewerEventsRef = useRef<(() => void) | null>(null);
  const reinitForPly = useCallback(async () => {
    const ctrl = controllerRef.current;
    const container = containerRef.current;
    if (!ctrl || !container) return;
    // 先解绑旧事件，避免 reinit 后监听器泄漏到新 viewer
    offViewerEventsRef.current?.();
    offViewerEventsRef.current = null;
    const avatarUrl = `${import.meta.env.BASE_URL}avatar/character.glb`;
    await ctrl.init({
      container,
      renderer,
      deviceType: 'webgl',
      avatarUrl,
      avatarEnabled: false,
      ministats,
    });
    offViewerEventsRef.current = bindViewerEvents(ctrl, trackRef);
  }, [renderer, ministats]);

  useNavigationController(controllerRef);
  useMeasureSync(controllerRef);
  useSnapshotCapture(controllerRef);
  useHotspotNavigation({ controllerRef });
  useCinematicEvents(controllerRef, urlParams);
  useImmersiveMode();
  useAssetPick(containerRef, controllerRef);

  const status = useViewerStore((s) => s.status);
  const navigationMode = useViewerStore((s) => s.navigationMode);

  const isEditing = useRaceTrackStore((s) => s.isEditing);
  const setPlaying = useRaceTrackStore((s) => s.setPlaying);
  const setOriginalTrack = useRaceTrackStore((s) => s.setOriginalTrack);

  // 备份原始运镜轨道，未编辑退出时可还原
  useEffect(() => {
    if (status === 'ready' && trackRef.current) {
      setOriginalTrack(trackRef.current);
    }
  }, [status, setOriginalTrack]);

  const handleClosePreview = useCallback(() => {
    useRaceTrackStore.getState().setPreviewOpen(false);
    setPlaying(false);
  }, [setPlaying]);

  // 场景就绪后自动聚焦容器，确保 iframe 内 keyboard 事件立即可用
  useEffect(() => {
    if (status === 'ready') {
      containerRef.current?.focus({ preventScroll: true });
    }
  }, [status]);

  // 门控：显式加载错误优先交给 HUD 错误层，避免网络断开时被发布状态页遮挡重试入口。
  if (status === 'not-found') return <SceneNotFoundPage />;
  if (status !== 'error' && publishGate) return publishGate;

  // h-screen 作为旧浏览器回退；移动端 Edge/Safari 等带动态底栏时用 dvh，
  // 避免底部工具栏落到浏览器地址栏/工具栏之后被遮挡。
  return (
    <div className="relative h-dvh h-screen w-screen overflow-hidden">
      {/* 3D canvas container — tabIndex 使其可接收焦点，确保键盘事件在 iframe 首次加载时即可用 */}
      <div ref={containerRef} tabIndex={-1} className="absolute inset-0 outline-none" />
      <HotspotLayer containerRef={containerRef} getController={getController} />

      {/* 无 taskId 时全屏拖拽区 */}
      <FileDropZone
        getController={getController}
        deviceType={deviceType}
        reinitForPly={reinitForPly}
      />

      {webglDialogOpen && (
        <VrWebglDialog
          onCancel={() => setWebglDialogOpen(false)}
          onConfirm={handleConfirmWebglReload}
        />
      )}

      {/* HUD overlay */}
      <HUDLayout
        onRetry={handleRetry}
        onToggleCinematic={handleToggleCinematic}
        onEnterVr={handleEnterVr}
        onSelectVrQuality={handleSelectVrQuality}
        getController={getController}
      />

      {/* Track Editor & Preview Floating Panels */}
      <TrackEditor getController={getController} />
      <TrackPreview onClose={handleClosePreview} getController={getController} />

      {/* 量测由 supersplat-viewer 原生 MeasureTool 处理，useMeasureSync 驱动开关 */}

      {/* 进度条位于工具栏正上方，水平居中；fly/walk/avatar 模式由 viewer 闭环中断运镜，
          UI 同步隐藏进度条避免误操作 seek。 */}
      {!isEditing &&
        (sceneConfig?.track || mockTrack) &&
        navigationMode !== 'fly' &&
        navigationMode !== 'walk' &&
        navigationMode !== 'avatar' && (
          <div className="pointer-events-none absolute bottom-[72px] left-1/2 z-20 w-full max-w-[500px] -translate-x-1/2 px-4 sm:px-0">
            <CinematicProgressBar
              duration={trackRef.current?.duration ?? sceneConfig?.track?.duration ?? trackDuration}
              onSeek={(t) => controllerRef.current?.navigation.anim.seek(t)}
            />
          </div>
        )}
    </div>
  );
}
